// Ask Dugzi, native in every product (CEO on review-suite-fix: "how could this be integrated as a
// native experience in all our products"). An app doesn't build its own AI: it offers a contextual
// action ("Ask about this thread", "Summarise this document", "Find a time", "Explain this formula")
// and calls `askDugzi(prompt)`. The app launcher (present in every app) opens the Ask Dugzi panel
// beside the app with that question, in the same account. Nothing is sent before the person has
// turned Ask Dugzi on; the panel then shows the consent screen first, with the question waiting.
import type { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { productAvailable } from '../release-scope';
import { Tooltip } from './tooltip';

export const ASK_EVENT = 'dugzi:ask';

export interface AskRequest {
  /** The question, in the person's words as far as possible: "Summarise the thread “Boiler service”". */
  prompt: string;
  /** Send it straight away (true, the default) or leave it in the box to edit first. */
  send?: boolean;
}

/** Opens Ask Dugzi beside the app with this question (via the app launcher). */
export function askDugzi(prompt: string, opts: { send?: boolean } = {}): void {
  if (!productAvailable('ai')) return;
  const detail: AskRequest = { prompt: prompt.trim().slice(0, 500), send: opts.send ?? true };
  if (!detail.prompt) return;
  window.dispatchEvent(new CustomEvent<AskRequest>(ASK_EVENT, { detail }));
}

/** The short modifier label for the panel shortcut: ⌘J on Apple devices, Ctrl+J elsewhere. */
export function askShortcut(): string {
  return /Mac|iPhone|iPad/.test(typeof navigator === 'undefined' ? '' : navigator.platform) ? '⌘J' : 'Ctrl+J';
}

/**
 * A contextual Ask Dugzi action: the same look in every app (sparkle, sentence-case verb, soft
 * accent), so people learn it once. `compact` shows the icon only (with the label as its name).
 */
export function AskDugziAction({ label, prompt, send = true, compact, className, testId = 'ask-action', children }: {
  label: string; prompt: string | (() => string); send?: boolean; compact?: boolean; className?: string; testId?: string; children?: ReactNode;
}) {
  if (!productAvailable('ai')) return null;
  const button = (
    <button type="button" className={`dz-ask-action${compact ? ' dz-ask-action--compact' : ''}${className ? ` ${className}` : ''}`}
      aria-label={compact ? label : undefined} data-testid={testId}
      onClick={() => askDugzi(typeof prompt === 'function' ? prompt() : prompt, { send })}>
      <Sparkles aria-hidden size={16} />
      {!compact && <span>{children ?? label}</span>}
    </button>
  );
  // Icon only: the name shows as our tooltip, never the browser's `title=` (D-062).
  return compact ? <Tooltip content={label}>{button}</Tooltip> : button;
}
